<script setup>
import dayjs from 'dayjs'
import { ref, onMounted, onUnmounted, computed, nextTick, watch } from 'vue'

import { getI18n } from '@/plugins/i18n'

import { DxSortable } from 'devextreme-vue/sortable'

import { formatDayMonthDate, stringToColor, textColor } from '@/lib/utils'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore, searchResultTypeEnum } from '@/stores/web-site.store'
import { useChatStore } from '@/stores/chat.store'

import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import { useNavigator } from '@/composables/navigator'
import { useCurrencyColumnFormatter } from '@/composables/data-grid'
import { useShare } from '@/composables/share'
import { useArchive } from '@/composables/archive'
import { useChips } from '@/composables/chips'
import { useNewBudgetDocumentRow, useBudgetDocumentRowLabels } from '@/composables/budget-document-row'
import { useHighlightNewRow } from '@/composables/scroll'
import { useRouter } from 'vue-router'
import { menuRoutes, menuRouteEnum } from '@/router'

import ActionsMenuButton from '@/components/shared/ActionsMenuButton.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import ListingFirstToggle from '@/components/shared/ListingFirstToggle.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import DescriptionSection from '@/components/shared/DescriptionSection.vue'

import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import BudgetDocumentEdit from './BudgetDocumentEdit.vue'
import BudgetDocumentDetailEdit from '@/components/accounting/BudgetDocumentDetailEdit.vue'
import BudgetDocumentPrintDialog from '@/components/accounting/BudgetDocumentPrintDialog.vue'
import BudgetDocumentRowListItem from '@/components/accounting/BudgetDocumentRowListItem.vue'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'

const props = defineProps({
  itemId: {
    type: Number,
    required: true,
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
})
useChatStore().trackPageEntity(searchResultTypeEnum.QUOTE, () => props.itemId)


const i18n = getI18n()

const emit = defineEmits(['update', 'cancel', 'menu-actions'])
useNavigator(() => {
  if (!editItem.value && !budgetDocumentEditItem.value) {
    emit('cancel')
  }
})
const { isMobile } = useDetectDevice()

// Mirror the CSS breakpoint exactly so JS and CSS are always in sync
const _mql = window.matchMedia('(max-width: 820px)')
const isMobileLayout = ref(_mql.matches)
const _onMqlChange = (e) => { isMobileLayout.value = e.matches }
_mql.addEventListener('change', _onMqlChange)
onUnmounted(() => _mql.removeEventListener('change', _onMqlChange))

const rowsInteractive = computed(() => !item.value?.archived && (!isMobileLayout.value || editMode.value))
const isRowEditable = (rowItem) => rowsInteractive.value && rowItem.invoice == null
const { notifyWarning, notifySuccess, notifyInfo } = useNotify()
const { getBudgetDocumentEstimateChip } = useChips()
const { newBudgetDocumentRow } = useNewBudgetDocumentRow()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()

const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()

const share = useShare()

const editItem = ref(null)
const rowsContainer = ref(null)
const budgetDocumentEditItem = ref(null)
const deleteConfirmRow = ref(null)
const showDeleteRowDialog = computed({
  get: () => deleteConfirmRow.value !== null,
  set: (val) => { if (!val) deleteConfirmRow.value = null },
})
const deleteRowMessage = computed(() => deleteConfirmRow.value?.has_project_listing_item
  ? i18n.t('warning.deleteRowLinkedToProject')
  : i18n.t('common.confirmDelete'))
const includeVat = ref(true)

const item = ref((await accountingStore.api.getBudgetDocumentsAsync({ id: props.itemId, all: true })).data[0])
const listing = planningStore.getListings().find(l => l.id === item.value.listing)
const groupRowsBy = ref(item.value.group_rows_by)
const groupByName = computed(() => i18n.t(accountingStore.budgetDocumentGroupRowsByLabels[groupRowsBy.value]))

const availableGroups = ref([])
const editMode = ref(false)
const showListingFirst = ref(webSiteStore.getDefaultShowListingFirst())
const defaultAddGroup = computed(() => availableGroups.value[availableGroups.value.length - 1] ?? { id: 1, rows: [] })
const rowLabels = useBudgetDocumentRowLabels({
  showListingFirst,
  groupRowsBy,
  hasListing: () => !!item.value.listing,
})

const updateDatasource = async() => {
  
  item.value = (await accountingStore.api.getBudgetDocumentsAsync({ id: props.itemId, all: true })).data[0]
  
  const result = (await accountingStore.api.getBudgetDocumentRowsAsync({ budget_document: props.itemId, is_group_child: false })).data
  
  result.sort((a, b) => a.row_number - b.row_number)
  
  if (groupRowsBy.value === accountingStore.budgetDocumentGroupRowsByEnum.PRIMARY_TAG) {
    const localizedTags = accountingStore.getLocalizedBudgetTags()

    const groupMap = new Map()

    result.forEach(r => {
      const tags = r.tags?.sort() || []
      const key = tags.join(',')
      if (!groupMap.has(key)) groupMap.set(key, [])
      groupMap.get(key)?.push(r)
    })

    availableGroups.value = Array.from(groupMap.entries()).map(([key, rows]) => {
      const tagIds = key ? key.split(',').map(id => Number(id)) : []
      const title = tagIds.length
        ? tagIds.map(id => localizedTags.find(t => t.id === id)?.name || '').join(', ')
        : i18n.t('common.untagged')

      return {
        id: key || '0',
        title,
        rows
      }
    })
    // const primaryTags = [null, ...new Set(result.map(r => r.tags).flat())]

    // availableGroups.value = primaryTags.map(tag => {
    //   return {
    //     id: tag || 0,
    //     title: accountingStore.getLocalizedBudgetTags().find(t => t.id === tag)?.name || i18n.t('common.untagged'),
    //     rows: tag ? result.filter(r => r.tags.includes(tag)) : result.filter(r => !r.tags || r.tags.length === 0)
    //   }
    // })
    
  } 
  else if (groupRowsBy.value === accountingStore.budgetDocumentGroupRowsByEnum.DAY) {
    const days = dayjs(item.value.end).diff(dayjs(item.value.start), 'day') + 1

    availableGroups.value = [...Array(days).keys()].map(d => {
      const date = dayjs(item.value.start).add(d, 'day')
      return {
        id: d,
        date: date,
        title: `${formatDayMonthDate(date)}, ${i18n.t('common.day')} ${d + 1}`,
        rows: result.filter(r => dayjs(r.date).isSame(date, 'day'))
      }
    })
    availableGroups.value.unshift({
      id: -1,
      date: null,
      title: i18n.t('common.unassigned'),
      rows: result.filter(r => !r.date)
    })
  }
  else {
    availableGroups.value = [{ id: 1, title: null, rows: result }]
  }
}


onMounted(async() => {
  const fetches = []
  if (planningStore.staff.length === 0) fetches.push(planningStore.fetchStaffsAsync({ all: true }, true))
  if (planningStore.equipments.length === 0) fetches.push(planningStore.fetchEquipmentsAsync({ all: true }, true))
  if (planningStore.groups.length === 0) fetches.push(planningStore.fetchGroupsAsync({ all: true }, true))
  await Promise.all(fetches)
  await rowLabels.loadListingItemsAsync()
  await updateDatasource()
  emit('menu-actions', parentMenuActions.value)
})


const onAddClick = (groupItem, documentRowType = webSiteStore.documentRowTypeEnum.LISTING_ITEM) => {
  editItem.value = {
    ...newBudgetDocumentRow({
      budgetDocumentId: props.itemId,
      listingId: item.value.listing,
      currencyRef: item.value.currency_ref,
      documentRowType,
    }),
    day: groupItem.id > 0 ? groupItem.id : null,
    date: groupRowsBy.value === accountingStore.budgetDocumentGroupRowsByEnum.DAY && groupItem.date ? groupItem.date.format('YYYY-MM-DD') : null,
    tags: groupRowsBy.value === accountingStore.budgetDocumentGroupRowsByEnum.PRIMARY_TAG && groupItem.id ? groupItem.id.split(',').map(id => Number(id)).filter(i => i > 0) : [],
  }
}

const onRowEditUpdate = async () => {
  const wasCreated = !editItem.value?.id
  const previousIds = new Set(availableGroups.value.flatMap(g => g.rows.map(r => r.id)))
  editItem.value = null
  await updateDatasource()
  if (wasCreated) {
    const newRow = availableGroups.value.flatMap(g => g.rows).find(r => !previousIds.has(r.id))
    await highlightRowAsync(newRow?.id, rowsContainer)
  }
}

const reloadBudgetDocumentAsync = async() => {
  item.value = (await accountingStore.api.getBudgetDocumentsAsync({ id: props.itemId })).data[0]
}

const onBudgetDocumentUpdate = async() => {
  await reloadBudgetDocumentAsync()
  budgetDocumentEditItem.value = null
}

const onIconClick = (e) => { deleteConfirmRow.value = e }

const onConfirmDeleteRow = async () => {
  await accountingStore.api.deleteBudgetDocumentRowAsync(deleteConfirmRow.value.id)
  deleteConfirmRow.value = null
  await updateDatasource()
}

const onGroupRowsByUpdated = async (value) => {
  groupRowsBy.value = value === true ? accountingStore.budgetDocumentGroupRowsByEnum.DAY : value === false ? accountingStore.budgetDocumentGroupRowsByEnum.NONE : accountingStore.budgetDocumentGroupRowsByEnum.PRIMARY_TAG

  await accountingStore.api.upsertBudgetDocumentAsync({
    id: item.value.id,
    group_rows_by: groupRowsBy.value
  })

  await updateDatasource()
}

const onDragEnd = (e) => {
  const fromGroupId = e.fromData
  const toGroupId = e.toData
  const fromData = availableGroups.value.find(g => g.id === fromGroupId)?.rows
  const toData = availableGroups.value.find(g => g.id === toGroupId)?.rows

  if (!fromData || !toData) return
  const itemData = fromData[e.fromIndex]
  const toDataItem = toData[e.toIndex]
  if (!itemData) return

  nextTick(async() => {
    if (groupRowsBy.value === accountingStore.budgetDocumentGroupRowsByEnum.DAY) {
      const toGroup = availableGroups.value.find(g => g.id === toGroupId)
      await accountingStore.api.upsertBudgetDocumentRowAsync({
        id: itemData.id,
        budget_document_id: props.itemId,
        date: toGroup?.date ? dayjs(toGroup.date).format('YYYY-MM-DD') : null,
      })
    }
    else if (groupRowsBy.value === accountingStore.budgetDocumentGroupRowsByEnum.PRIMARY_TAG) {
      await accountingStore.api.upsertBudgetDocumentRowAsync({
        id: itemData.id,
        budget_document_id: props.itemId,
        tags: toGroupId ? toGroupId.split(',').map(id => Number(id)) : [],
      })
    }

    // Same endpoint the desktop grid uses: it renumbers the whole document 1..n.
    if (toDataItem && toDataItem.id !== itemData.id) {
      await accountingStore.api.reorderBudgetDocumentRowsAsync(props.itemId, { fromId: itemData.id, toId: toDataItem.id })
    }

    await updateDatasource()
  })

  fromData.splice(e.fromIndex, 1)
  toData.splice(e.toIndex, 0, itemData)
}

const getGroupStyle = (groupItem) => {
  return groupItem.rows.length === 0 ? 'min-height: 2rem; border: 1px dashed var(--row-card-border-color); border-radius: 0.5rem; margin: 0.5rem;' : ''
}

const showPrintDialog = ref(false)

const shareLinkAsync = (url) => share.shareLinkAsync(url, i18n.t('common.budgetDocument'), (url, title, isCopiedToClipboard) => {
  if (isCopiedToClipboard) {
    notifySuccess(i18n.t('common.magicLinkCopiedToClipboard'))
  } else {
    notifySuccess(i18n.t('common.magicLinkHere', { link: url }), {
      html: true,
      timeout: 0,
    })
  }
})

const onMagicLinkClick = async () => {
  let { url } = (await accountingStore.getBudgetDocumentMagicLinkAsync(item.value.id)).data

  if (url) {
    notifyInfo(i18n.t('common.existingMagicLinkFound'), {
      actions: [
        { label: i18n.t('common.ok'), color: 'white', handler: async () => { await shareLinkAsync(url) } },
        { label: i18n.t('common.generateNewLink'), color: 'white', bold: true, 
          handler: async () => {
            url = (await accountingStore.printBudgetDocumentMagicLinkAsync(item.value.id, { group_by: groupRowsBy.value } )).data?.url
            await shareLinkAsync(url)
          } 
        },
        { label: i18n.t('common.cancel'), color: 'white', handler: () => {} },
      ],
      timeout: 0,
    })
  } else await shareLinkAsync(url)
}

const router = useRouter()

const { archiveWarning, archiveAsync } = useArchive({
  onArchive: async(id) => { await accountingStore.archiveBudgetDocumentAsync(id) },
  onDelete: async(id, config) => { await accountingStore.deleteBudgetDocumentAsync(id, config) },
})

const onArchive = async(item) => {
  await archiveAsync(item.id, item.archived)
  item.archived = !item.archived
}

const onCreateProject = async () => {
  if (item.value.is_estimate) {
    notifyWarning(i18n.t('warning.cannotCreateProjectFromEstimateDocument'))
    return
  }
  await accountingStore.generateProjectFromBudgetDocumentAsync(item.value.id)
  notifySuccess(i18n.t('common.projectCreated'))
  router.push(menuRoutes[menuRouteEnum.PROJECTS])
}

// Header dots-menu actions — data-driven so an embedding parent (e.g. ProjectDetail tabs,
// which hides this header) can surface the same behaviour in its own header menu.
const menuActions = computed(() => {
  const actions = []
  if (!item.value.archived) {
    actions.push({ key: 'print', label: i18n.t('common.printQuote'), icon: 'mdi-printer', handler: () => { showPrintDialog.value = true } })
    actions.push({ key: 'createProject', label: i18n.t('common.createProject'), icon: 'mdi-briefcase-plus-outline', handler: onCreateProject })
    actions.push({ key: 'magicLink', label: i18n.t('common.magicLink'), icon: 'mdi-link-variant', handler: onMagicLinkClick })
  }
  actions.push({ key: 'archive', label: i18n.t(item.value.archived ? 'common.unarchive' : 'common.archive'), icon: item.value.archived ? 'mdi-archive-arrow-up' : 'mdi-archive', handler: () => onArchive(item.value) })
  return actions
})

// The full set (incl. edit) an embedding parent surfaces in its own header menu.
const parentMenuActions = computed(() => {
  const editAction = item.value.archived
    ? []
    : [{ key: 'edit', label: i18n.t('common.editBudgetDocument'), icon: 'mdi-pencil', handler: () => { budgetDocumentEditItem.value = { ...item.value } } }]
  return [...editAction, ...menuActions.value]
})

// Push the actions to the parent (keeps them reactive to archive/edit state changes).
watch(parentMenuActions, (actions) => emit('menu-actions', actions))


useEntityChanges([entityChangeTypeEnum.QUOTE], (change) => (!change.id || change.id === props.itemId) && reloadBudgetDocumentAsync(), () => budgetDocumentEditItem.value !== null)
</script>
<template>
  <q-dialog v-model="showPrintDialog" :maximized="isMobile" persistent>
    <budget-document-print-dialog
      v-if="showPrintDialog"
      :budget-document-id="item.id"
      @cancel="showPrintDialog = false"
    />
  </q-dialog>
  <confirm-dialog
    v-model="showDeleteRowDialog"
    :message="deleteRowMessage"
    icon="mdi-delete-outline"
    icon-color="negative"
    @confirm="onConfirmDeleteRow"
  />
  <archive-warning-dialog
    v-model="archiveWarning"
    @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
  />
  <q-dialog
    :model-value="editItem !== null"
    class="dialog-xs"
    :maximized="isMobile"
    persistent
  >
    <budget-document-detail-edit
      v-if="editItem !== null"
      :model-value="editItem"
      :budget-document="item"
      :listing-id="listing?.id || null"
      @update="onRowEditUpdate"
      @cancel="editItem = null"
    />
  </q-dialog>
  <q-dialog
    :model-value="budgetDocumentEditItem !== null"
    class="dialog-xs"
    :maximized="isMobile"
    persistent
  >
    <budget-document-edit
      v-if="budgetDocumentEditItem !== null"
      :model-value="budgetDocumentEditItem"
      @update="onBudgetDocumentUpdate"
      @cancel="onBudgetDocumentUpdate"
      @delete="budgetDocumentEditItem = null; emit('update')"
    />
  </q-dialog>

  <q-card class="dialog-card-flex">
    <navigate-back
      v-if="!hideHeader"
      :text="item?.name"
      :archived="item?.archived"
      @cancel="emit('cancel')"
    >
      <template #right>
        <div style="display: flex; align-items: center;">
          <q-btn
            v-if="!item.archived"
            flat
            round
            icon="mdi-pencil"
            @click="budgetDocumentEditItem = {...item}"
          />
          <actions-menu-button :actions="menuActions" />
        </div>
      </template>
    </navigate-back>
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll">
      <q-card-section class="q-pt-none q-px-sm">
        <q-list>
          <q-item>
            <div style="width: 50%; float: left;">
              <q-item-section>
                <q-item-label caption>
                  {{ $t('common.listing') }}
                </q-item-label>
                <q-item-label class="align-items-baseline">
                  {{ item.listing ? listing?.name : '-' }}
                </q-item-label>
              </q-item-section>
            </div>
            <div style="width: 50%; float: right;">
              <q-item-section style="display: flex; align-content: flex-end;">
                <q-item-label
                  caption
                  style="text-align: right;"
                >
                  {{ $t('common.value') }}
                </q-item-label>
                <q-item-label style="text-align: right;">{{ useCurrencyColumnFormatter(item, includeVat ? 'total_vat' : 'total_no_vat', item.currency_ref) }}</q-item-label>
              </q-item-section>
            </div>
          </q-item>
          <q-item v-if="!hideHeader">
            <div style="width: 50%; float: left;">
              <q-item-section>
                <q-item-label caption>
                  {{ $t('common.type') }}
                </q-item-label>
                <q-item-label class="align-items-baseline">
                  <chip-template
                    :values="[getBudgetDocumentEstimateChip(item.is_estimate)]"
                    :style="{ 'margin-left': '0' }"
                  />
                </q-item-label>
              </q-item-section>
            </div>
            <div style="width: 50%; float: right;">
              <q-item-section style="display: flex; align-content: flex-end;">
                <q-item-label caption style="text-align: right;">
                  {{ $t('common.period') }}
                </q-item-label>
                <q-item-label style="text-align: right;">
                  <q-icon
                    name="mdi-clock-outline"
                    class="q-mr-xs"
                  />
                  <span>
                    {{ dayjs(item.start).format('HH:mm') }} - {{ dayjs(item.end).format('HH:mm') }}
                  </span>
                </q-item-label>
                <q-item-label class="align-items-baseline" style="text-align: right;">
                  <q-icon
                    name="mdi-calendar"
                    class="q-mr-xs"
                  />
                  <span>
                    {{ formatDayMonthDate(item.start) }} to {{ formatDayMonthDate(item.end) }}
                  </span>
                </q-item-label>
              </q-item-section>
            </div>
          </q-item>
          <q-item v-if="item.tag_ids?.length">
            <q-item-section>
              <q-item-label caption>
                {{ i18n.t('common.tag', 2) }}
              </q-item-label>
              <q-item-label>
                <chip-template
                  :values="(item.tag_ids || []).map(id => {
                    const tag = accountingStore.getLocalizedBudgetTags().find(t => t.id === id)
                    if (!tag) return null
                    const color = stringToColor(tag.name)
                    return { id, name: tag.name, color, textColor: textColor(color) }
                  }).filter(Boolean)"
                  :style="{ 'margin-left': '0' }"
                />
              </q-item-label>
            </q-item-section>
          </q-item>
          <description-section v-if="item.description && !hideHeader" :text="item.description" />
        </q-list>
      </q-card-section>
      <q-separator />
      <q-card-section style="display: flex; justify-content: space-between;">
        <q-toggle
          toggle-indeterminate
          keep-color
          :disable="item.archived"
          :class="{ 'group-by-day-toggle': groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.DAY, 'group-by-primary-tag-toggle': groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.PRIMARY_TAG, 'ungrouped-toggle': groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.NONE}"
          :icon="groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.DAY ? 'mdi-calendar' : groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.PRIMARY_TAG ? 'mdi-tag-multiple-outline' : 'mdi-view-list-outline'"
          :model-value="groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.DAY ? true : groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.PRIMARY_TAG ? null : false"
          :label="groupByName"
          @update:model-value="onGroupRowsByUpdated"
        />
        <q-toggle
          v-model="includeVat"
          :disable="item.archived"
          :label="i18n.t(includeVat ? 'common.includeVat' : 'common.excludeVat')"
        />
      </q-card-section>
      <q-separator />
      <div v-if="!item.archived" class="rows-action-bar">
        <q-btn
          v-if="isMobileLayout"
          flat
          round
          dense
          :icon="editMode ? 'mdi-pencil-off' : 'mdi-pencil'"
          :color="editMode ? 'primary' : 'grey-6'"
          @click="editMode = !editMode"
        >
          <q-tooltip>{{ editMode ? $t('common.done') : $t('common.edit') }}</q-tooltip>
        </q-btn>
        <listing-first-toggle v-model="showListingFirst" />
        <div style="flex: 1;" />
        <q-btn
          style="padding: 0 .5rem;"
          size="sm"
          color="info"
          unelevated
          ripple
          rounded
          no-caps
          icon="mdi-plus-circle-outline"
          @click="onAddClick(defaultAddGroup, webSiteStore.documentRowTypeEnum.COMMENT)"
        >&nbsp;{{ $t('common.documentRowTypes.comment') }}</q-btn>
        <q-btn
          style="padding: 0 .5rem;"
          size="sm"
          color="positive"
          unelevated
          ripple
          rounded
          no-caps
          icon="mdi-plus-circle-outline"
          @click="onAddClick(defaultAddGroup, webSiteStore.documentRowTypeEnum.EXTRA)"
        >&nbsp;{{ $t('common.documentRowTypes.extra') }}</q-btn>
        <q-btn
          style="padding: 0;"
          size="md"
          unelevated
          ripple
          rounded
          icon="mdi-plus-circle-outline"
          @click="onAddClick(defaultAddGroup)"
        />
      </div>
      <q-card-section class="q-pa-sm rows-section">
        <!-- Rows scroll area: constrained height on mobile, native scroll with left-side bar -->
        <div class="rows-scroll-area">
          <div ref="rowsContainer" class="rows-scroll-inner">
            <div v-for="groupItem in availableGroups" :key="groupItem.id">
              <div class="handle-group">
                <div v-if="groupRowsBy !== accountingStore.budgetDocumentGroupRowsByEnum.NONE" class="group-row">
                  <q-item-label style="font-weight: bolder">
                    <q-icon
                      :name="groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.DAY ? 'mdi-calendar' : 'mdi-tag-multiple-outline'"
                      class="q-mr-xs"
                    />{{ groupItem.title }}</q-item-label>
                </div>
                <!-- When rows are locked `filter` matches no child, so nothing is draggable: it is a
                     declared (reactive) prop, unlike `disabled`, and re-attaches the drag handlers.
                     Drag starts from the grip only: it is the one element with `touch-action: none`,
                     so on touch the browser does not steal the gesture for scrolling -->
                <dx-sortable
                  :key="groupItem.id"
                  :style="getGroupStyle(groupItem)"
                  :data="groupItem.id"
                  group="rows"
                  handle=".row-drag-handle"
                  :filter="rowsInteractive ? '> *' : '> .drag-disabled'"
                  :allow-reordering="rowsInteractive && groupItem.rows.length > 0"
                  @drag-end="onDragEnd($event)"
                >
                  <budget-document-row-list-item
                    v-for="rowItem in groupItem.rows"
                    :key="rowItem.id"
                    v-ripple="rowsInteractive"
                    :row="rowItem"
                    :labels="rowLabels"
                    :currency-ref="item.currency_ref"
                    :include-vat="includeVat"
                    :show-listing-first="showListingFirst"
                    :show-date="groupRowsBy !== accountingStore.budgetDocumentGroupRowsByEnum.DAY"
                    :class="{ 'q-ml-sm': groupRowsBy !== accountingStore.budgetDocumentGroupRowsByEnum.NONE, 'handle': true, 'row-highlight': rowItem.id === highlightedRowId }"
                    :hide-avatar="!rowsInteractive"
                    :icon="isRowEditable(rowItem) ? 'mdi-minus-circle-outline' : ''"
                    :icon-color="rowItem.has_project_listing_item ? 'grey' : 'negative'"
                    :clickable="isRowEditable(rowItem)"
                    @click="isRowEditable(rowItem) && (editItem = {...rowItem, day: groupItem.id > 0 ? groupItem.id : null})"
                    @icon-click="onIconClick(rowItem)"
                  >
                    <template #avatar>
                      <i class="dx-icon dx-icon-dragvertical row-drag-handle" @click.stop />
                    </template>
                  </budget-document-row-list-item>
                </dx-sortable>
              </div>
            </div>
          </div>
        </div>
      </q-card-section>
    </q-card-section>
  </q-card>
</template>
<style scoped lang="scss">
.group-row {
  padding-top: .5rem;
  padding-bottom: .5rem;
  display: flex;
  align-items: center;
}

:deep(.q-item__section--avatar:has(.row-drag-handle)) {
  min-width: 0;
  padding: 0 .25rem 0 0;
}

.row-drag-handle {
  touch-action: none;
  cursor: grab;
  color: $grey-6;
  font-size: 1.125rem;
  padding: .35rem .15rem;
}

.projects-toggle {
  cursor: pointer;
  & .q-toggle__thumb:after {
    background-color: $blue !important;
  }
  & .q-toggle__track {
    background-color: $blue !important;
  }
  & .q-toggle__thumb .q-icon{
    color: $white !important;
    opacity: 1 !important;
  }
}

:deep(.group-by-day-toggle) {
  cursor: pointer;
  & .q-toggle__thumb:after {
    background-color: $blue !important;
  }
  & .q-toggle__track {
    background-color: $blue !important;
    color: white !important;
  }
}

:deep(.group-by-primary-tag-toggle) {
  cursor: pointer;
  & .q-toggle__thumb:after {
    background-color: $positive !important;
  }
  & .q-toggle__track {
    background-color: $positive !important;
    color: white !important;
  }
}

:deep(.ungrouped-toggle) {
  cursor: pointer;
  & .q-toggle__thumb:after {
    background-color: $grey !important;
  }
  & .q-toggle__track {
    background-color: $grey !important;
    color: white !important;
  }
}

// Rows scroll area: natural height on desktop, flex fill on mobile with left scrollbar
.rows-action-bar {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: .5rem 1rem;
  padding-bottom: 0rem;
}

.rows-scroll-area {
  overflow: visible;
}

.rows-scroll-inner {
  direction: ltr;
  padding: 0 .5rem;
}

@media (max-width: 820px) {
  // Make the whole content area a non-scrolling flex column so rows fill remaining space
  .dialog-card-content-scroll {
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }

  // The rows section grows to fill all remaining space
  .rows-section {
    flex: 1 !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
    padding: .5rem !important;
  }

  .rows-scroll-area {
    flex: 1;
    direction: rtl;
    overflow-y: auto;
  }

  .rows-scroll-inner {
    direction: ltr;
  }
}
</style>
